import { SearchFilterValue } from '.';
import { MedicalDataTypes } from '../../../../primary-record-types';
import { getIntl } from '../../../../utils/intl';
import {
    allergyStatusMessages,
    conditionStatusMessages,
    pluralResourceCategoryMessages,
    resourceCategoryMessages,
} from '../../../../utils/sharedMessages';

const intl = getIntl('get-filter-summary', {
    upcoming: 'Upcoming',
    past: 'Past',
    allTime: 'All-Time',
    active: 'Active',
    inactive: 'Inactive',
    routine: 'Routine',
    'walk-in': 'Walk-In',
    checkup: 'Checkup',
    followup: 'Followup',
    emergency: 'Emergency',
    other: 'Other',
    all: 'All',
    showingResultsFor: 'Showing results for',
    list: 'List',
});

export type GetFilterSummaryArgs = {
    category: MedicalDataTypes.PrimaryRecordResourceCategory;
    value: SearchFilterValue;
    displayedCount: number;
    isLoading?: boolean;
};

export type Summary = {
    pretext: string;
    filter: string;
};

export const getFilterSummary = ({ category, value, displayedCount }: GetFilterSummaryArgs): Summary | null => {
    const getFilterParts = (): string[] => {
        if (category === 'allergy') {
            return value.allergyStatus ? [allergyStatusMessages(value.allergyStatus)] : [];
        } else if (category === 'condition') {
            return value.conditionStatus ? [conditionStatusMessages(value.conditionStatus)] : [];
        } else if (category === 'medication') {
            return value.medicationIsActive !== null
                ? [value.medicationIsActive ? intl('active') : intl('inactive')]
                : [];
        } else if (category === 'visit') {
            const parts: string[] = [];
            // Name the time window whenever it isn't the default month view.
            if (value.visitWindow && value.visitWindow !== 'next-month') {
                parts.push(intl(value.visitWindow === 'all' ? 'allTime' : value.visitWindow));
            }
            // 'all' is the unfiltered status - naming it ("All Visits") reads like a filter.
            if (value.visitStatus && value.visitStatus !== 'all') parts.push(intl(value.visitStatus));
            return parts;
        } else {
            return [];
        }
    };

    const filterParts = getFilterParts();

    // Visits always show a summary line (the View All shortcut sits on the same row);
    // other categories keep it filter-only.
    if (filterParts.length === 0 && category !== 'visit') {
        return null;
    }

    const categoryLabel =
        displayedCount === 1 ? resourceCategoryMessages(category) : pluralResourceCategoryMessages(category);
    return {
        pretext: intl('showingResultsFor'),
        filter: [`${displayedCount}`, filterParts.join(', '), categoryLabel].filter(Boolean).join(' '),
    };
};

export const getTabLabelSummary = ({ category, value, displayedCount, isLoading }: GetFilterSummaryArgs) => {
    if (category === 'lab') return intl('list');

    const maybeGetFilteredDescription = (): string | null => {
        if (category === 'allergy') {
            return value.allergyStatus ? allergyStatusMessages(value.allergyStatus) : null;
        } else if (category === 'condition') {
            return value.conditionStatus ? conditionStatusMessages(value.conditionStatus) : null;
        } else if (category === 'medication') {
            return value.medicationIsActive !== null
                ? value.medicationIsActive
                    ? intl('active')
                    : intl('inactive')
                : null;
        } else if (category === 'visit') {
            return value.visitStatus === 'all' ? intl('all') : value.visitStatus ? intl(value.visitStatus) : null;
        } else {
            return null;
        }
    };

    const filteredDescription = maybeGetFilteredDescription();
    return `${filteredDescription || intl('list')} (${isLoading ? '...' : displayedCount})`;
};
